// @flow
import React, { Component } from 'react'
import { translations, translateFunc } from '../../shared/localize'
import _ from 'lodash'
import FieldSet from '../FieldSet'
import utils from '../FieldSet/utils'
import type { Node } from '../types'
import LabeledWrapper from './LabeledWrapper'

const t = translateFunc(translations.demographics)

type Props = {|
  errors?: Array<string>,
  selectedIds: Array<number>,
  providerDemographics: Array<Object>,
  fbVisible: boolean,
  tmVisible: boolean,
  onSelectedIdsChange: (Array<number>) => void
|}

type State = {|
  isOpen: boolean,
  expandedIds: Object,
  selectedIds: Array<number>,
  searchString: string,
  demographicList: Array<Node>,
  demographicTrees: Array<Node>
|}

export default class Demographics extends Component<Props, State> {
  constructor(props: Props) {
    super(props)

    this.state = {
      isOpen: false,
      expandedIds: {},
      selectedIds: props.selectedIds,
      searchString: '',
      demographicList: [],
      demographicTrees: []
    }
  }

  componentDidUpdate(prevProps: Props) {
    if (
      _.xor(this.props.selectedIds, prevProps.selectedIds).length > 0 &&
      _.xor(this.state.selectedIds, this.props.selectedIds).length > 0
    ) {
      this.setState({ selectedIds: this.props.selectedIds }, this.updateStateCallback)
    }
  }

  componentWillMount() {
    this.updateStateCallback()
  }

  updateStateCallback() {
    const { providerDemographics } = this.props

    const demographicList = _.map(providerDemographics, (demographic) => {
      const { selectedIds } = this.state
      const { id, provider } = demographic

      return {
        ...demographic,
        provider: _.camelCase(provider),
        key: id,
        isSelected: _.includes(selectedIds, id),
        hideCheckbox: demographic.disabled
      }
    })

    const demographicTrees = utils.buildProviderTrees(demographicList)

    this.setState({ demographicList, demographicTrees })

    this.props.onSelectedIdsChange(this.state.selectedIds)
  }

  render() {
    const { demographicList } = this.state
    const demographicListSelected = _.filter(demographicList, 'isSelected')

    return (
      <LabeledWrapper label={t('label')} errors={this.props.errors}>
        <FieldSet.Layout>
          {this.renderSelected(demographicListSelected)}
          <FieldSet.OpenButton label={t('open_modal')} onClick={() => this.openModal()} />
          {this.renderModal()}
        </FieldSet.Layout>
      </LabeledWrapper>
    )
  }

  renderSelected(selectedList: Array<Node>) {
    const { facebook, targetMail } = _.groupBy(selectedList, 'provider')

    if (!this.props.tmVisible && !this.props.fbVisible) return null
    return (
      <FieldSet.Lines>
        {this.props.fbVisible && (
          <FieldSet.Line title={t('facebook_provider')}>
            {_.sortBy(facebook, 'label').map(this.renderSelectedInline)}
          </FieldSet.Line>
        )}
        {this.props.tmVisible && (
          <FieldSet.Line title={t('target_mail_provider')}>
            {_.sortBy(targetMail, 'label').map(this.renderSelectedInline)}
          </FieldSet.Line>
        )}
      </FieldSet.Lines>
    )
  }

  renderSelectedInline = (node: Node) => (
    <FieldSet.SelectedInline
      node={node}
      key={node.id}
      onDeselect={(node) => this.removeSelectedIds([node.id])}
    />
  )

  renderModal() {
    const { searchString, isOpen } = this.state

    // prevent rendering modal when it's closed
    if (!isOpen) return null

    return (
      <FieldSet.Modal
        isOpen={isOpen}
        onClose={() => this.closeModal()}
        onSearchChange={(searchString) => this.searchChanged(searchString)}
        searchString={searchString}
        header={t('select_demographics')}
        searchPlaceholder={t('search')}
        searchable
        maxWidth={800}
      >
        {_.isEmpty(searchString) ? this.renderTrees() : this.renderMatchedNodes()}
      </FieldSet.Modal>
    )
  }

  renderTrees(): any {
    const { facebook, targetMail } = _.groupBy(this.state.demographicTrees, 'provider')

    return (
      <FieldSet.Columns>
        {this.props.fbVisible && (
          <FieldSet.Column leftPadding title={t('facebook_provider')}>
            {this.renderTree(facebook)}
          </FieldSet.Column>
        )}
        {this.props.tmVisible && (
          <FieldSet.Column title={t('target_mail_provider')}>
            {this.renderTree(targetMail)}
          </FieldSet.Column>
        )}
      </FieldSet.Columns>
    )
  }

  renderTree(nodes: Array<Node>): Array<Function> {
    return _.sortBy(nodes, 'label').map((node) => (
      <FieldSet.Node
        node={node}
        key={node.id}
        expandedIds={this.state.expandedIds}
        triggerNodeExpanded={(id, expanded) => this.triggerNodeExpanded(id, expanded)}
        triggerNodeSelected={(node) => this.triggerNodeSelected(node)}
      />
    ))
  }

  renderMatchedNodes(): any {
    const { demographicList } = this.state

    const mathchedNodes = this.matchedNodes(demographicList)
    const mathchedNodeIds = _.map(mathchedNodes, 'id')
    const { facebook, targetMail } = _.groupBy(mathchedNodes, 'provider')

    return (
      <div>
        <FieldSet.MatchedAction
          label={t('select_all')}
          onClick={() => this.addSelectedIds(mathchedNodeIds)}
        />
        <FieldSet.MatchedAction
          label={t('clear_all')}
          onClick={() => this.removeSelectedIds(mathchedNodeIds)}
        />

        <FieldSet.Columns>
          {this.props.fbVisible && (
            <FieldSet.Column title={t('facebook_provider')}>
              {this.renderMatchedNodesByProvider(facebook)}
            </FieldSet.Column>
          )}
          {this.props.tmVisible && (
            <FieldSet.Column title={t('target_mail_provider')}>
              {this.renderMatchedNodesByProvider(targetMail)}
            </FieldSet.Column>
          )}
        </FieldSet.Columns>
      </div>
    )
  }

  renderMatchedNodesByProvider(nodes: Array<Node>): Array<Function> {
    nodes = nodes || []

    return _.map(nodes, (node) => (
      <FieldSet.MatchedNode
        node={node}
        key={node.id}
        onSelectChange={(node) => this.triggerNodeSelected(node)}
      />
    ))
  }

  triggerNodeSelected(node: Node) {
    const { isSelected, id } = node

    this.setState((state) => {
      const { selectedIds, expandedIds } = state

      if (isSelected) {
        const parents = utils.findParents(node, this.state.demographicList)
        const father = parents[0]

        if (father && _.isUndefined(expandedIds[father.id])) {
          const idsToExpand = _.reduce(
            parents,
            (merged, p) => _.merge(merged, { [p.id]: true }),
            {}
          )
          return {
            selectedIds: _.without(selectedIds, id),
            expandedIds: { ...idsToExpand, ...expandedIds }
          }
        } else {
          return { selectedIds: _.without(selectedIds, id) }
        }
      } else {
        return { selectedIds: _.uniq([...selectedIds, id]) }
      }
    }, this.updateStateCallback)
  }

  removeSelectedIds(ids: Array<number>) {
    this.setState((state) => {
      return { selectedIds: _.without(state.selectedIds, ...ids) }
    }, this.updateStateCallback)
  }

  addSelectedIds(ids: Array<number>) {
    this.setState((state) => {
      return { selectedIds: _.uniq([...state.selectedIds, ...ids]) }
    }, this.updateStateCallback)
  }

  triggerNodeExpanded(id: number, expanded: boolean) {
    this.setState((state) => {
      return {
        expandedIds: { ...state.expandedIds, [id]: expanded }
      }
    }, this.updateStateCallback)
  }

  openModal() {
    this.setState({ isOpen: true })
  }

  closeModal() {
    this.setState({ isOpen: false })
  }

  matchedNodes(interestList: Array<Node>): Array<Node> {
    const { searchString } = this.state
    const matcherFunc = utils.searchStringMatcher(searchString)
    return _.filter(interestList, ({ label, disabled }) => !disabled && matcherFunc(label))
  }

  searchChanged(searchString: string) {
    this.setState({ searchString }, this.updateStateCallback)
  }
}
